/**
 * VireoHR - Signup Screen
 * Ultra-Minimal Design
 */

import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { auth, firestore } from '@/lib/firebase/client';


export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Error state
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    termsAccepted: ''
  });

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return true; // Optional field

    // Jordan format: +9627XXXXXXXX or 07XXXXXXXX
    const jordanPhoneRegex = /^(\+9627|07)\d{8}$/;
    return jordanPhoneRegex.test(phone);
  };

  const validatePassword = (pwd: string): boolean => {
    // Min 8 chars, at least one letter and one number
    return pwd.length >= 8 && /[a-zA-Z]/.test(pwd) && /\d/.test(pwd);
  };

  const validateForm = (): boolean => {
    const newErrors = {
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      termsAccepted: ''
    };

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(password)) {
      newErrors.password = 'Password must be at least 8 characters with letters and numbers';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = 'Enter a valid Jordan phone number (+9627XXXXXXXX or 07XXXXXXXX)';
    }

    if (!termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create Firestore user document
      await setDoc(doc(firestore, 'users', user.uid), {
        uid: user.uid,
        email: email,
        phoneNumber: phoneNumber || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        defaultTenantId: null,
        tenantId: null,
        role: 'owner',
        isManager: false,
        managerLocationIds: [],
        onboardingStatus: 'ACCOUNT_CREATED'
      });

      toast.success('Account created successfully!');

      // Redirect to business setup
      navigate('/signup/business');

    } catch (error: any) {
      console.error('Signup error:', error);

      // Handle Firebase Auth errors
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ ...errors, email: 'This email is already registered. Try logging in instead.' });
        toast.error('Email already in use');
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ ...errors, email: 'Enter a valid email address.' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ ...errors, password: 'Password must be at least 8 characters with letters and numbers.' });
      } else {
        toast.error(`Error: ${error.message || 'Signup failed'}`);
      }

      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-[#0D9488] mb-2">Create your VireoHR account</h1>
          <p className="text-sm text-[#D4AF37]">
            This account will manage your company's employees, attendance, and payroll.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#D4AF37] mb-1">
              Email *
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            />
            {errors.email && (
              <p className="text-xs text-red-600 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            />
            {errors.password && (
              <p className="text-xs text-red-600 mt-1">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number (Optional)
            </label>
            <input
              id="phoneNumber"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+9627XXXXXXXX or 07XXXXXXXX"
              className="w-full px-3 py-2 border border-[#D4AF37] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0D9488]"
              disabled={loading}
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-600 mt-1">{errors.phoneNumber}</p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 text-[#0D9488] border-[#D4AF37] rounded focus:ring-[#0D9488] mt-0.5"
                disabled={loading}
              />
              <span className="text-sm text-[#D4AF37]">
                I accept the{' '}
                <a href="/terms" className="text-[#0D9488] hover:underline">
                  Terms and Conditions
                </a>
              </span>
            </label>
            {errors.termsAccepted && (
              <p className="text-xs text-red-600 mt-1">{errors.termsAccepted}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0D9488] text-white hover:bg-[#0a7d73]"
          >
            {loading ? 'Creating account...' : 'Continue'}
          </Button>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#D4AF37]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0D9488] hover:underline font-medium">
              Log in
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}
